import React, { useEffect, useState } from 'react';
import { getSettings, saveSettings, SettingGroup, SettingField } from '../api/strategy';
import './StrategyReports.css';
import './RollingControl.css';

// Admin Settings page — tune engine knobs from the UI without a redeploy.
// Precedence: what you set here (UI) wins → else the .env value on the server → else the built-in
// default. Clear a field (leave it blank) to fall back to env. Groups marked "live" apply on the
// next engine cycle; no restart.
const Settings: React.FC = () => {
  const [groups, setGroups] = useState<SettingGroup[] | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      const { groups } = await getSettings();
      setGroups(groups);
      // Seed the edit buffer with the current UI overrides (blank where none).
      const seed: Record<string, string> = {};
      for (const g of groups) for (const f of g.fields) seed[f.key] = f.override ?? '';
      setEdits(seed);
    } catch (e: any) {
      setError(e?.message || 'Could not load settings');
    }
  };
  useEffect(() => { load(); }, []);

  const setEdit = (key: string, value: string) => {
    setEdits((e) => ({ ...e, [key]: value }));
    setSavedMsg(null);
  };

  const dirty = groups
    ? groups.some((g) => g.fields.some((f) => (edits[f.key] ?? '') !== (f.override ?? '')))
    : false;

  const save = async () => {
    if (!groups) return;
    setSaving(true); setError(null); setSavedMsg(null);
    try {
      // Send only the fields the user actually changed.
      const overrides: Record<string, string> = {};
      for (const g of groups) for (const f of g.fields) {
        const next = edits[f.key] ?? '';
        if (next !== (f.override ?? '')) overrides[f.key] = next;
      }
      const res = await saveSettings(overrides);
      const parts: string[] = [];
      if (res.applied.length) parts.push(`${res.applied.length} set`);
      if (res.cleared.length) parts.push(`${res.cleared.length} cleared (fell back to env)`);
      if (res.rejected.length) parts.push(`${res.rejected.length} rejected`);
      setSavedMsg(parts.length ? `Saved — ${parts.join(', ')}. Live on the next engine cycle.` : 'No changes.');
      if (res.rejected.length) setError('Rejected: ' + res.rejected.map((r) => `${r.key} (${r.reason})`).join('; '));
      await load();
    } catch (e: any) {
      setError(e?.message || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const sourceBadge = (f: SettingField) => {
    const src = (edits[f.key] ?? '') !== '' ? 'ui' : f.source;
    const color = src === 'ui' ? '#166534' : src === 'env' ? '#92400e' : '#6b7280';
    const bg = src === 'ui' ? '#dcfce7' : src === 'env' ? '#fef3c7' : '#f3f4f6';
    const label = src === 'ui' ? 'UI' : src === 'env' ? 'env' : 'default';
    return <span style={{ fontSize: 11, fontWeight: 700, color, background: bg, borderRadius: 4, padding: '1px 6px' }}>{label}</span>;
  };

  const fallbackText = (f: SettingField) => {
    if (f.env_value != null && f.env_value !== '') return `env: ${f.env_value}`;
    if (f.default != null) return `default: ${String(f.default)}`;
    return 'unset';
  };

  const renderInput = (f: SettingField) => {
    const val = edits[f.key] ?? '';
    if (f.type === 'bool') {
      return (
        <select className="rc-select" value={val} onChange={(e) => setEdit(f.key, e.target.value)} style={{ minWidth: 150 }}>
          <option value="">— use {fallbackText(f)} —</option>
          <option value="true">On</option>
          <option value="false">Off</option>
        </select>
      );
    }
    return (
      <input
        type={f.type === 'number' ? 'number' : 'text'}
        value={val}
        placeholder={fallbackText(f)}
        min={f.min ?? undefined}
        max={f.max ?? undefined}
        step={f.step ?? undefined}
        onChange={(e) => setEdit(f.key, e.target.value)}
        style={{ minWidth: 150, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6 }}
      />
    );
  };

  return (
    <div className="sr-page">
      <div className="sr-head">
        <h1>Settings</h1>
        <div className="sr-sub">
          Tune the engine from here — no redeploy. A value you set wins over the server's <code>.env</code>,
          which wins over the built-in default. Leave a field blank to fall back to env.
        </div>
      </div>

      {error && <div className="sr-error">{error}</div>}

      <div className="rc-btn-row" style={{ margin: '8px 0 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="rc-btn on" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Saving…' : dirty ? 'Save changes' : 'No changes'}
        </button>
        {savedMsg && <span className="rc-note" style={{ color: '#166534' }}>{savedMsg}</span>}
      </div>

      {!groups && <p className="sr-empty">Loading…</p>}

      {groups && groups.map((g) => (
        <div className="sr-section" key={g.id} style={{ marginBottom: 20 }}>
          <h2>
            {g.title}{' '}
            {g.live && <span style={{ fontSize: 11, fontWeight: 700, color: '#166534', background: '#dcfce7', borderRadius: 4, padding: '1px 6px' }}>live · next cycle</span>}
          </h2>
          <div className="sr-section-hint">{g.blurb}</div>
          <table className="sr-table" style={{ marginTop: 8 }}>
            <thead><tr>
              <th style={{ width: '30%' }}>Setting</th>
              <th style={{ width: '20%' }}>Value</th>
              <th style={{ width: '10%' }}>In force</th>
              <th>What it does</th>
            </tr></thead>
            <tbody>
              {g.fields.map((f) => (
                <tr key={f.key}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{f.label}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{f.key}</div>
                  </td>
                  <td>{renderInput(f)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 600 }}>{f.effective ?? '—'}</span>
                      {sourceBadge(f)}
                    </div>
                  </td>
                  <td style={{ fontSize: 13, color: '#4b5563' }}>{f.help}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="rc-note" style={{ marginTop: 8 }}>
        Only these curated engine knobs are settable here — secrets, database and connection settings are
        never exposed. More groups can be added on request.
      </div>
    </div>
  );
};

export default Settings;
